import { Button, Skeleton, Stack } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { useEffect, useState } from "react";
import messagesIllustration from "resources/images/messages-illustration.png";
import { useAppSelector } from "utils/redux/hooks";
import { selectCurrentUserAccountId } from "app/containers/Global/slice";

import {
  useActiveCheckInConversation,
  useOpenConversation,
} from "../../../common";
import { EmptyStateCard, StyledCard } from "../../layer01";
import { BoBrainMemoryBlock } from "../../layer02";

import * as ds from "./dataService";

type BoBrainSectionListProps = {
  search: string;
  startDate?: string;
  endDate?: string;
};

const PAGE_SIZE = 5;

const MemoryBlockSkeleton = () => (
  <Stack
    direction="column"
    rowGap=".5rem"
    padding={{
      xs: ".75rem",
      sm: "1rem",
    }}
    borderRadius=".5rem"
    bgcolor="#F7F7F7"
  >
    <Skeleton width="45%" height={24} />
    <Stack direction="column" rowGap=".125rem">
      <Skeleton width="95%" height={16} />
      <Skeleton width="90%" height={16} />
      <Skeleton width="60%" height={16} />
    </Stack>
  </Stack>
);

export const BoBrainSectionList = ({
  search,
  startDate,
  endDate,
}: BoBrainSectionListProps) => {
  const openConversation = useOpenConversation();
  const editConversation = ds.useEditConversation();
  const activeCheckInConversation = useActiveCheckInConversation(
    useAppSelector(selectCurrentUserAccountId)
  );
  const { data, totalCount, isLoading, historyStatus } =
    ds.useFilteredConversationsQuery({ search, startDate, endDate });
  const [totalItemsShowing, setTotalItemsShowing] = useState(PAGE_SIZE);
  const visibleItems = data.slice(0, totalItemsShowing);

  useEffect(() => {
    setTotalItemsShowing(PAGE_SIZE);
  }, [search, startDate, endDate]);

  const getContent = () => {
    if (historyStatus === "companyDisabled") {
      return (
        <EmptyStateCard
          image={messagesIllustration}
          title="Conversation history is turned off"
          body="Your organization has turned off conversation history, so conversation summaries aren't available."
        />
      );
    }

    if (isLoading) {
      return [0, 1, 2].map((i) => <MemoryBlockSkeleton key={i} />);
    }

    if (historyStatus === "userDisabled") {
      return (
        <EmptyStateCard
          image={messagesIllustration}
          title="Conversation history is turned off"
          body="You've chosen not to save conversations with Bo."
        />
      );
    }

    if (totalCount === 0) {
      return (
        <EmptyStateCard
          image={messagesIllustration}
          title="No conversation summaries yet"
          body="Chats you have with Bo will show up here."
        />
      );
    }

    if (data.length === 0) {
      return (
        <EmptyStateCard
          image={messagesIllustration}
          title="No results"
          body="No conversations match your filters."
        />
      );
    }

    return (
      <>
        {visibleItems.map((item) => {
          const isCheckIn = !!item.conversation.weeklyCheckInLink;
          const getResumeButtonText = () => {
            if (!isCheckIn) {
              return "Resume";
            }
            // The active check-in can still be answered; past ones are read-only.
            return item.id === activeCheckInConversation?.threadId
              ? "Edit"
              : "View";
          };

          return (
            <BoBrainMemoryBlock
              key={item.id}
              title={item.displayTitle}
              editTitle={item.title}
              body={item.body}
              editBody={item.summary}
              onResume={() => openConversation(item.conversation)}
              resumeButtonText={getResumeButtonText()}
              onSave={
                item.canEdit && !isCheckIn
                  ? (title, summary) =>
                      editConversation(item.conversation, title, summary)
                  : undefined
              }
            />
          );
        })}
        {totalItemsShowing < data.length ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setTotalItemsShowing((s) => s + PAGE_SIZE);
            }}
            className="border-0"
          >
            View More
            <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
          </Button>
        ) : null}
      </>
    );
  };

  return (
    <Stack direction="column" rowGap=".75rem">
      <StyledCard title="Recent conversations">
        <Stack direction="column" rowGap="1rem" minWidth={0}>
          {getContent()}
        </Stack>
      </StyledCard>
    </Stack>
  );
};
